import {
  InvoiceDocumentWithSummary,
  InvoiceDocumentWithSummaryConnectionInput,
  InvoiceDocumentWithSummarySearch,
  InvoiceSearch,
  SearchTypeEnum,
} from '@/models/gen/graphql';

import { SearchInvoiceDocumentTableDocument } from '@/api/queries';
import createGraphApiHook from '@/hooks/createGraphApiHook';
import graphApi from '@/api';
import { searchInvoices } from './searchInvoices';

const [, runRefetchSearchInvoiceDocument] = graphApi(SearchInvoiceDocumentTableDocument);

export type PartialInvoiceDocumentWithSummary = Partial<InvoiceDocumentWithSummary>;

export type InvoiceDocumentWithSummarySearchAndFullNames = Partial<InvoiceDocumentWithSummary> & {
  createdByFullName: string;
  sentByFullName: string;
};
const searchInvoiceDocumentTable = async (...query: InvoiceDocumentWithSummarySearch[]) => {
  const input: InvoiceDocumentWithSummaryConnectionInput = {
    first: 1000,
    after: null,
    query,
  };
  const res = await runRefetchSearchInvoiceDocument({ input });
  const invoices =
    res.searchInvoiceDocument?.invoiceDocumentTableConnection?.edges?.map?.((edge) => edge.node) || ([] as InvoiceDocumentWithSummary[]);
  const invoicesWithFullNames: InvoiceDocumentWithSummarySearchAndFullNames[] = invoices.map((invoice) => ({
    ...invoice,
    createdByFullName: [invoice.createdByFirstName, invoice.createdByLastName].filter(Boolean).join(' '),
    sentByFullName: [invoice.sentByFirstName, invoice.sentByLastName].filter(Boolean).join(' '),
  }));
  return {
    invoices: invoicesWithFullNames,
    totalInvoices: res.searchInvoiceDocument?.invoiceDocumentTableConnection?.totalCount || 0,
  };
};

export const useSearchInvoiceDocument = createGraphApiHook(searchInvoiceDocumentTable);

export type InvoiceDocumentWithSummaryAndAmendments = InvoiceDocumentWithSummarySearchAndFullNames & {
  history: PartialInvoiceDocumentWithSummary[];
};
export const searchInvoiceDocumentGroupedByAmendment = async (
  ...query: InvoiceDocumentWithSummarySearch[]
): Promise<{ invoices: InvoiceDocumentWithSummaryAndAmendments[]; totalInvoices: number }> => {
  // Step 1: Get all documents the way we are currently getting them
  const { invoices } = await searchInvoiceDocumentTable(...query);

  // Step 2: Get all of the documents that don't have an amendment
  const invoicesWithoutAmendment = invoices.filter((invoice) => !invoice.amendment);

  let enrichedInvoices = invoices;

  // Step 3: Search for the invoices that have those ones without an amendment by their invoiceId
  if (invoicesWithoutAmendment.length > 0) {
    const invoiceIdsToSearch = Array.from(
      new Set(invoicesWithoutAmendment.map((invoice) => invoice.invoiceId).filter(Boolean) as number[])
    );

    // Search invoices (not invoice documents) by invoice IDs
    const searchByIdQuery: InvoiceSearch = {
      id: [
        {
          values: invoiceIdsToSearch,
          type: SearchTypeEnum.Or,
          sort: null,
        },
      ],
    };

    const { rows: searchedInvoices } = await searchInvoices([searchByIdQuery]);

    // Step 4: Replace missing data in the invoices without amendments with the data in the searched invoices
    // Create a map of invoice data by invoice ID
    const invoiceDataMap = new Map<number, (typeof searchedInvoices)[0]>();
    searchedInvoices.forEach((invoice) => {
      if (invoice.id) {
        invoiceDataMap.set(invoice.id, invoice);
      }
    });

    enrichedInvoices = invoices.map((invoice) => {
      if (!invoice.amendment && invoice.invoiceId && invoiceDataMap.has(invoice.invoiceId)) {
        const invoiceData = invoiceDataMap.get(invoice.invoiceId)!;
        return {
          ...invoice,
          chargeType: invoiceData.chargeType as any,
          uniqueId: invoiceData.uniqueId ?? undefined,
          payerProviderDisplayName: invoiceData.provider?.displayName ?? undefined,
          airports: invoiceData.airports ? invoiceData.airports.join(',') : undefined,
          dueDatetime: invoiceData.due,
          invoiceDate: invoiceData.invoiced,
          startDatetime: invoiceData.startDatetime,
          endDatetime: invoiceData.endDatetime,
          grandTotal: invoiceData.grandTotal,
        };
      }
      return invoice;
    });
  }

  // Step 5: Group as we are doing now
  const groupedByAmendment: InvoiceDocumentWithSummaryAndAmendments[] = groupInvoices(enrichedInvoices);
  return {
    invoices: groupedByAmendment,
    totalInvoices: groupedByAmendment.length,
  };
};

const groupInvoices = (invoices): InvoiceDocumentWithSummaryAndAmendments[] => {
  const grouped: Record<string, InvoiceDocumentWithSummaryAndAmendments[]> = {};

  // Group by base invoiceId
  invoices.forEach((invoice) => {
    const baseId = invoice.invoiceId;
    if (!grouped[baseId]) {
      grouped[baseId] = [];
    }
    if (!invoice.amendment) {
      const existing = grouped[baseId].at(0);
      if (existing) {
        const latest = [invoice, existing].sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()).at(0);
        grouped[baseId][0] = latest;
      } else {
        grouped[baseId].push(invoice);
      }
    } else {
      grouped[baseId].push(invoice);
    }
  });

  return Object.values(grouped).map((group) => {
    // Sort by amendment version (latest first)
    const sorted = [...group].sort(
      (a, b) => getAmendmentVersion(b.amendment || `${b.invoiceId}`) - getAmendmentVersion(a.amendment || `${a.invoiceId}`)
    );
    const [latest, ...rest] = sorted;
    return {
      ...latest,
      history: rest,
    };
  });
};

const getAmendmentVersion = (amendment: string) => {
  const parts = amendment.split('-');
  if (parts.length === 1) return 0; // original invoice
  return parts[1].charCodeAt(0) - 64; // A=1, B=2, etc.
};

export const useSearchInvoiceDocumentGroupedByAmendment = createGraphApiHook(searchInvoiceDocumentGroupedByAmendment);

export default searchInvoiceDocumentTable;
